/**
 * Campaign audience entries are polymorphic on the backend
 * (`[{ id, type }]`, where type is `Label` or `AudienceList`), but the
 * multi-select combobox only carries a flat list of scalar values. These helpers
 * encode the type into the option value (`label:12`, `list:3`) and decode it
 * back into the API payload, so every channel form shares one contract.
 */

export const AUDIENCE_TYPES = {
  LABEL: 'Label',
  AUDIENCE_LIST: 'AudienceList',
};

const LABEL_PREFIX = 'label';
const LIST_PREFIX = 'list';

export const labelAudienceValue = id => `${LABEL_PREFIX}:${id}`;
export const audienceListValue = id => `${LIST_PREFIX}:${id}`;

/**
 * Builds combobox options from labels and audience lists. Lists are prefixed so
 * they are visually distinct from labels inside a single flat dropdown, and are
 * listed first because they are the explicit, operator-curated audience.
 */
export const buildAudienceOptions = ({
  labels = [],
  audienceLists = [],
  listPrefixLabel = '',
} = {}) => [
  ...audienceLists.map(list => ({
    value: audienceListValue(list.id),
    label: listPrefixLabel ? `${listPrefixLabel} · ${list.name}` : list.name,
  })),
  ...labels.map(label => ({
    value: labelAudienceValue(label.id),
    label: label.title,
  })),
];

/**
 * Decodes selected combobox values into the `audience` payload. Plain numeric
 * values are still accepted and treated as labels so any caller that has not
 * migrated to the prefixed form keeps working.
 */
export const toAudiencePayload = (values = []) =>
  values
    .map(value => {
      if (typeof value === 'number') {
        return { id: value, type: AUDIENCE_TYPES.LABEL };
      }

      const [prefix, rawId] = String(value).split(':');
      const rawValue = rawId ?? prefix;
      if (!rawValue) return null;

      const id = Number(rawValue);
      if (Number.isNaN(id)) return null;

      if (prefix === LIST_PREFIX) {
        return { id, type: AUDIENCE_TYPES.AUDIENCE_LIST };
      }
      return { id, type: AUDIENCE_TYPES.LABEL };
    })
    .filter(Boolean);

/** Encodes an existing `audience` payload back into combobox values. */
export const fromAudiencePayload = (audience = []) =>
  audience.map(entry =>
    entry.type === AUDIENCE_TYPES.AUDIENCE_LIST
      ? audienceListValue(entry.id)
      : labelAudienceValue(entry.id)
  );
